import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  brand,
  focus,
  fontMono,
  inkOn,
  line,
  rgba,
  radius,
  semantic,
  space,
  tracking,
  typeRole,
  weight,
} from '@repo/design-tokens';
import { Banner, Button, CHANGE_GLYPH, Dot, Icon, Note, Skeleton, Tabs } from '@repo/design-system';
import type { TagTone } from '@repo/design-system';
import type { FloorPlannerItem, FloorPlannerSurface } from '@repo/frontend-types';
import { surfaceName } from '@repo/front-end-utils';
import {
  visuallyHidden,
  buildHierarchyTree,
  KindGlyph,
  HierarchyEntityRow,
  HierarchyTreeRows,
  hierarchyRowPropsEqual,
  CameraPicture,
  ListGroup,
  useRowMotion,
  useRowMotionAnnouncement,
  useKeepFocusAcrossMove,
  rowMotionGapLook,
  type CameraWallFeed,
  type FrameRegion,
  type HierarchyEntityRowProps,
  type HierarchyNode,
  type HierarchyRowSlots,
} from '@repo/ui';
import type { ObserverTargetKind } from './observerTypes';

import { NEEDS_ATTENTION } from './observerAttention';
import { clockTime } from './DraftStatusTag';
import { GROUP_LOOK, GROUP_ORDER, OBSERVER_STATE_ROLE, STATUS_DOT } from './observerStageModel';
import { NAME_COLLATOR, sortInventoryRows, typeHeading } from './inventorySort';
import type { InventorySortKey, SortDirection } from './inventorySort';
import type { AttentionState, EntityAttention } from './observerAttention';
import {
  FEEDS_LOADING,
  FEEDS_UNREADABLE_BODY,
  FEEDS_UNREADABLE_TITLE,
  NO_FEEDS_BODY,
} from './observerStageModel';

/**
 * A live region's text, written AFTER the region mounts.
 *
 * A `role="status"` element inserted with its text already in it is not reliably announced, so a
 * region that mounts with a view — the camera workspace's filter line, the editor's status line on
 * *Choose a camera* — renders empty on its first commit and takes its text from an effect. Every
 * later change is an ordinary text change in a region that already exists.
 */
export const SettledText: React.FC<{ text: string }> = ({ text }) => {
  const [shown, setShown] = useState('');
  useEffect(() => setShown(text), [text]);
  return <>{shown}</>;
};

/** Name first, as the plan reads it (`surfaceName`; epic `onboarding-polish` ledger row 27). */
const surfaceLabel = (s: FloorPlannerSurface) => surfaceName(s) || 'Untitled zone';
const itemLabel = (i: FloorPlannerItem) => i.name || 'Equipment';

type View = 'inventory' | 'hierarchy' | 'cameras';
/** Each view's name, for the panel its tabs control. */
const VIEW_LABELS: Record<View, string> = {
  inventory: 'Inventory',
  hierarchy: 'Hierarchy',
  cameras: 'Cameras',
};

/* ------------------------------------------------------------------ */
/* Shared row                                                          */
/* ------------------------------------------------------------------ */

/**
 * A row here: the shared row's own props (`@repo/ui` `HierarchyEntityRowProps`), less its slots,
 * plus what only this page shows in them.
 */
type ObserverEntityRowProps = Omit<
  HierarchyEntityRowProps,
  'marker' | 'details' | 'slotKey' | 'current' | 'announceSelected'
> & {
  observerCount: number;
  /**
   * The observer read is still in flight, so this row's count is not yet known.
   *
   * ⚠ **Not cosmetic.** A row with no observers renders no badge at all, so during the load every
   * row looked exactly like a row with nothing watching it — the same "a pending read reads as an
   * absence" defect this feature exists to remove, in miniature. While this is true the row says it
   * is still counting instead.
   */
  countsLoading?: boolean;
  /**
   * This entity's attention state (`camera-observer-setup-attention`). The marker lives on the ROW,
   * so the Inventory groups, the Hierarchy tree, the Cameras view and the copy embedded in the
   * observer editor all carry it without further work — an entity is never marked in one view and
   * unmarked in another. Omitted, the row renders as it always did.
   */
  attention?: EntityAttention;
  /**
   * Where this entity's observers are, in the camera workspace (`workspace-inventory-this-camera`,
   * R4) — "2 on this camera · 1 on another camera". Its own calm line after the state; omitted
   * everywhere a camera is not chosen.
   */
  whereText?: string;
  /** The Hierarchy's status dot (FR13), a token colour; null or absent draws none. */
  dot?: string | null;
};

/**
 * The glyph for the attention states, in its state's role ink (`onboarding-status-colours-observers`,
 * R5): work to do for No observer and Added, a problem for Needs checking. Hidden from assistive
 * technology by Icon's own rule: the adjacent state text carries the meaning.
 */
const AttentionIcon = styled(Icon)<{ $ink: string }>`
  color: ${({ $ink }) => $ink};
  flex-shrink: 0;
`;

/** An attention state's glyph ink: its role's, from the one state-to-role table (R1). */
const attentionInk = (state: AttentionState): string => {
  const role = OBSERVER_STATE_ROLE[state];
  return role && role !== 'done'
    ? semantic.status.roles[role].ink
    : semantic.status.roles.workToDo.ink;
};

export const AttentionGlyph: React.FC<{ state: AttentionState }> = ({ state }) => (
  <AttentionIcon
    name="circle-exclamation"
    size="sm"
    className="attention-glyph"
    $ink={attentionInk(state)}
  />
);

/**
 * The added mark beside an added entity's attention glyph (R4): the added glyph in the lasting
 * just-changed ink, as Equipment's register draws it. The glyph is decorative; the hidden word says it.
 */
const AddedMark = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  color: ${semantic.status.lastingMark.ink};
`;

/*
 * What an observer can watch: a zone, a machine — or a whole FLOOR (`refiling-preserves-
 * configuration`, epic ledger row 121). Widened in the app rather than in `@repo/ui`'s
 * `ReadOnlyEntityKind`, which other planner canvases also use and which never shows a floor.
 * Defined in `observerTypes.ts` so the pure helpers can name it without importing this panel.
 */
export type { ObserverTargetKind };

/**
 * The words on a row — the state as TEXT, which is the channel a screen reader gets.
 *
 * Supersedes nothing: {@link attentionText} stays, because it has one caller left that reads its
 * output as a CLAUSE (*"{reason} since this observer was drawn"* in the editor), which only parses
 * for the three attention wordings. This is the row's version, and it answers two more questions:
 * what totals an entity nothing watches directly, and when an entity is watched in several pieces.
 *
 * A partial group is not a problem and carries no glyph — filing derives a composite that adds its
 * regions up. It is said because "3 observers" and "measured in 3 parts" are the same fact and only
 * one of them explains itself.
 */
export const entityStateText = (
  attention: EntityAttention | undefined,
  observerCount: number,
): string => {
  if (attention && NEEDS_ATTENTION.has(attention.state)) return attentionText(attention);
  if (attention?.state === 'totalled') return 'totalled by a complex measure';
  if (attention?.state === 'configured' && observerCount > 1) {
    return `measured in ${observerCount} parts`;
  }
  return '';
};

/** The words beside the glyph — the state as TEXT, which is the channel a screen reader gets. */
export const attentionText = (attention: EntityAttention): string => {
  switch (attention.state) {
    case 'added-unobserved':
      return 'new, no observer';
    case 'unobserved':
      return 'no observer';
    case 'check':
      return attention.reason ?? 'needs checking';
    default:
      return '';
  }
};

/*
 * The row itself is the shared onboarding-list row (`@repo/ui` `HierarchyEntityRow`, moved there
 * from this file by `onboarding-polish-plan-hierarchy` — move, never fork). What only this page
 * shows reaches it through the row's two slots: the Hierarchy's status dot on the kind glyph, and
 * the observer count (or the still-counting placeholder), the state line and the where-line inside
 * its button. Their styles are the panel's own (below). The key is what the row's memo compares in
 * their place, so it carries every value they show.
 */
const observerSlots = ({
  kind,
  observerCount,
  countsLoading = false,
  attention,
  whereText,
  dot,
}: Pick<
  ObserverEntityRowProps,
  'kind' | 'observerCount' | 'countsLoading' | 'attention' | 'whereText' | 'dot'
>): HierarchyRowSlots => {
  const marker =
    dot && attention && attention.state !== 'pending' ? (
      /* Named by its GROUP, not the row's state text, which the row already says in words. */
      <Dot className="kind-dot" color={dot} label={GROUP_LOOK[attention.state].title} labelHidden />
    ) : undefined;
  const details = (
    <>
      {countsLoading ? (
        <span className="row-observers counting" title="Checking for observers…">
          <Icon name="eye" variant="solid" size="xs" />
          <span aria-hidden="true">…</span>
          {/* The canonical recipe from `@repo/ui`, not a local copy — its own header states the
              rule ("nothing adds a new one"), and the copy written here first had already lost the
              `clipPath` half that keeps it working in Safari. */}
          <span style={visuallyHidden}>Checking for observers</span>
        </span>
      ) : (
        observerCount > 0 && (
          <span className="row-observers" title={`${observerCount} observer(s)`}>
            <Icon name="eye" variant="solid" size="xs" />
            {observerCount}
            {/* A floor row says what the number counts; its glyph and title are not read out. */}
            {kind === 'floor' && (
              <span style={visuallyHidden}>{observerCount === 1 ? ' observer' : ' observers'}</span>
            )}
          </span>
        )
      )}
      {/* Last in DOM order so the wrap puts name, code and count on one line and the state on the
          next — measured: placed earlier, the count wrapped onto a third line by itself.

          The glyph is tied to NEEDS_ATTENTION, not to the presence of text: `totalled` and
          `measured in N parts` are statements about what IS measured, and an error glyph beside
          them would say the opposite of the words. */}
      {!countsLoading && entityStateText(attention, observerCount) && (
        <span
          className={
            attention && NEEDS_ATTENTION.has(attention.state) ? 'row-state' : 'row-state calm'
          }
        >
          {attention && NEEDS_ATTENTION.has(attention.state) && (
            <AttentionGlyph state={attention.state} />
          )}
          {attention?.state === 'added-unobserved' && (
            <AddedMark className="added-mark">
              <Icon name={CHANGE_GLYPH.added} size="xs" />
              <span style={visuallyHidden}>added, </span>
            </AddedMark>
          )}
          {entityStateText(attention, observerCount)}
        </span>
      )}
      {/* A statement of fact, like "measured in N parts": calm, no glyph, its own line. */}
      {!countsLoading && whereText && <span className="row-state calm">{whereText}</span>}
    </>
  );
  const slotKey = [
    kind,
    observerCount,
    countsLoading ? 'counting' : '',
    attention?.state ?? '',
    entityStateText(attention, observerCount),
    whereText ?? '',
    dot ?? '',
  ].join('|');
  return { marker, details, slotKey };
};

/**
 * One row with its observer slots: the shared row, fed from this page's observer props. Memoised by
 * the shared row's own rule (`hierarchyRowPropsEqual`), so a plan hover skips the rows it does not
 * touch before any slot is built — exactly what this page's row cost before the move.
 */
const ObserverEntityRowBase: React.FC<ObserverEntityRowProps> = ({
  observerCount,
  countsLoading,
  attention,
  whereText,
  dot,
  ...row
}) => {
  const slots = observerSlots({
    kind: row.kind,
    observerCount,
    countsLoading,
    attention,
    whereText,
    dot,
  });
  return (
    <HierarchyEntityRow
      {...row}
      marker={slots.marker}
      details={slots.details}
      slotKey={slots.slotKey}
    />
  );
};
const ObserverEntityRow = React.memo(
  ObserverEntityRowBase,
  hierarchyRowPropsEqual as unknown as (
    prev: ObserverEntityRowProps,
    next: ObserverEntityRowProps,
  ) => boolean,
);

/* ------------------------------------------------------------------ */
/* Inventory view — the four attention groups (FR8)                    */
/* ------------------------------------------------------------------ */

interface Row {
  refid: string;
  kind: ObserverTargetKind;
  label: string;
  code?: string;
  observerCount: number;
  attention?: EntityAttention;
  /** The camera workspace's where-line (R4); empty elsewhere. */
  where?: string;
}

interface SectionProps {
  className?: string;
  title: string;
  icon: React.ReactNode;
  rows: Row[];
  /** What an empty section says. A group that is empty is a FACT about the twin, said in words. */
  emptyCopy?: string;
  /** Hide the count — the group's answer is not known (the running twin could not be read). */
  hideCount?: boolean;
  /** The group's pastel tone (FR9); absent, the title is plain text (the floor's own section). */
  tone?: TagTone;
  countsLoading?: boolean;
  /** The plan's hovered entity (FR15). */
  hoveredId?: string | null;
  /** Under the type sort, ZONES / MACHINES sub-headers where a group holds both (FR12). */
  typeHeadings?: boolean;
  selectedId: string | null;
  onSelect: (refid: string, kind: ObserverTargetKind) => void;
}

const Section: React.FC<SectionProps> = ({
  className,
  title,
  icon,
  rows,
  emptyCopy = 'None placed yet',
  hideCount = false,
  tone,
  countsLoading,
  hoveredId,
  typeHeadings = false,
  selectedId,
  onSelect,
}) => {
  const bodyId = useId();
  const mixed = typeHeadings && new Set(rows.map(r => r.kind)).size > 1;

  return (
    <div className={className}>
      {/*
       * The shared onboarding list group (`@repo/ui` `ListGroup`, moved here from this file by
       * `composites-page-list` — move, never fork): a chevron, the group's toned Tag pill with its
       * dot and its count inside it (card 05, *Tag with a count*), or the plain title for the
       * floor's own section. Assembled from existing parts (maria@twinlabs.ai, 2026-10-04).
       */}
      <ListGroup
        id={bodyId}
        title={title}
        tone={tone}
        icon={icon}
        {...(hideCount ? {} : { count: rows.length })}
      >
        <div className="section-body">
          {rows.length === 0 ? (
            <p className="empty-row">{emptyCopy}</p>
          ) : (
            rows.map((row, at) => (
              <React.Fragment key={row.refid}>
                {mixed && (at === 0 || rows[at - 1].kind !== row.kind) && (
                  <p className="type-heading" aria-hidden="true">
                    {typeHeading(row.kind)}
                  </p>
                )}
                <ObserverEntityRow
                  refid={row.refid}
                  kind={row.kind}
                  label={row.label}
                  code={row.code}
                  observerCount={row.observerCount}
                  countsLoading={countsLoading}
                  attention={row.attention}
                  whereText={row.where}
                  selected={selectedId === row.refid}
                  hovered={hoveredId === row.refid}
                  leading={<span className="expander-spacer" />}
                  onSelect={() => onSelect(row.refid, row.kind)}
                />
              </React.Fragment>
            ))
          )}
        </div>
      </ListGroup>
    </div>
  );
};

const StyledSection = styled(Section)`
  border-bottom: 1px solid ${line[2]};

  /* Horizontal inset from the spacing scale, and half what it was: row 130's "less left padding". */
  .section-body {
    padding: 0 ${space[2]} ${space[2]};
  }

  .empty-row {
    margin: 0;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #a3a3a3;
    font-style: italic;
  }

  /* ZONES / MACHINES under the type sort (FR12): mono caps, as the v3 page draws them. */
  .type-heading {
    margin: ${space[1]} 0 0;
    padding: 0 ${space[2]};
    font-family: ${fontMono};
    font-size: ${typeRole.caption2.fontSize};
    letter-spacing: ${tracking.label};
    color: ${semantic.text.muted};
  }
`;

/* ------------------------------------------------------------------ */
/* Hierarchy view — nested tree                                        */
/* ------------------------------------------------------------------ */

/**
 * This page's names for a zone and a machine, for the shared tree builder (`@repo/ui`
 * `buildHierarchyTree`) — epic ledger row 27 owns changing them.
 */
const OBSERVER_LABELS = { surface: surfaceLabel, item: itemLabel };

/**
 * What the camera workspace's lists show (`camera-first-observers`, R13-R18): the rows in view of
 * the camera, in the order given. The floor row is not filtered — a placed camera sees its own floor.
 */
export const filterRowsToSight = <T extends { refid: string }>(
  rows: readonly T[],
  inView: ReadonlySet<string>,
): T[] => rows.filter(row => inView.has(row.refid));

/**
 * The Hierarchy tree pruned to what the camera sees (R15): a node stays when it is in view OR any
 * descendant is, so a machine keeps its parent zone and the tree keeps its shape — that zone is
 * listed even when the camera does not see it.
 */
export const pruneTreeToSight = <T extends { refid: string; children: T[] }>(
  nodes: readonly T[],
  inView: ReadonlySet<string>,
): T[] =>
  nodes.flatMap(node => {
    const children = pruneTreeToSight(node.children, inView);
    return inView.has(node.refid) || children.length > 0 ? [{ ...node, children }] : [];
  });

/**
 * Where an entity's observers are, said on its row in the camera workspace
 * (`workspace-inventory-this-camera`, R4): `here` complete regions on this camera of `total` on
 * every camera. Empty when it has none — an unobserved row says so in its own state.
 */
export const whereText = (here: number, total: number): string => {
  if (total <= 0) return '';
  if (here <= 0) return 'only on other cameras';
  const elsewhere = Math.max(0, total - here);
  if (elsewhere === 0) return `${here} on this camera`;
  return elsewhere === 1
    ? `${here} on this camera · 1 on another camera`
    : `${here} on this camera · ${elsewhere} on other cameras`;
};

/**
 * What the camera workspace lists beyond the cone (`workspace-inventory-this-camera`, R1): every
 * entity with a region on this camera, and the picked one — as a KEY, sorted, so the listed set is
 * rebuilt only when its membership changes. A vertex drag rebuilds the counts record on every
 * pointer move; keyed on the record, the Hierarchy would be re-pruned on each one (NFR1).
 */
export const listedKey = (
  observersOnThisCamera: Readonly<Record<string, number>> | undefined,
  selectedId: string | null,
): string => {
  const ids = Object.entries(observersOnThisCamera ?? {})
    .filter(([, n]) => n > 0)
    .map(([refid]) => refid);
  if (selectedId) ids.push(selectedId);
  return Array.from(new Set(ids)).sort().join('\n');
};

/**
 * The set a placed camera's lists are narrowed to (R1): the cone plus {@link listedKey}'s members, or
 * `null` when there is no cone. A hook so the memo itself is what its test observes (NFR1): the set
 * keeps its identity across a re-render that changes only the counts — a vertex drag — so the
 * Hierarchy is not re-pruned on every pointer move.
 *
 * `cone` must be the host's memoised set, not a value rebuilt per render.
 */
export const useListedSet = (
  cone: ReadonlySet<string> | null,
  observersOnThisCamera: Readonly<Record<string, number>> | undefined,
  selectedId: string | null,
): ReadonlySet<string> | null => {
  const extraKey = listedKey(observersOnThisCamera, selectedId);
  return useMemo(
    () => (cone ? new Set([...Array.from(cone), ...extraKey.split('\n').filter(Boolean)]) : null),
    [cone, extraKey],
  );
};

/** A stable empty group, so a memoised row list never sees a fresh `[]`. */
const NO_ROWS: Row[] = [];

/** An empty group while the lists are narrowed to the camera: a fact about the cone, not the twin. */
const NONE_IN_VIEW = 'None in view of this camera';

interface ObserverTreeRowsProps {
  nodes: HierarchyNode[];
  depth: number;
  expanded: Set<string>;
  observerCounts: Record<string, number>;
  attention: Record<string, EntityAttention>;
  /** The camera workspace's where-line per row (R4); returns '' elsewhere. */
  whereFor: (refid: string) => string;
  countsLoading?: boolean;
  selectedId: string | null;
  hoveredId?: string | null;
  onToggle: (refid: string) => void;
  onSelect: (refid: string, kind: ObserverTargetKind) => void;
}

/**
 * The Hierarchy view's tree: the shared tree rows (`@repo/ui` `HierarchyTreeRows`), with this
 * page's observer count, state line and status dot in each row's slots.
 */
const ObserverTreeRows: React.FC<ObserverTreeRowsProps> = ({
  nodes,
  depth,
  expanded,
  observerCounts,
  attention,
  whereFor,
  countsLoading,
  selectedId,
  hoveredId,
  onToggle,
  onSelect,
}) => {
  const selectedIds = useMemo(() => new Set(selectedId ? [selectedId] : []), [selectedId]);
  return (
    <HierarchyTreeRows
      nodes={nodes}
      depth={depth}
      expanded={expanded}
      selectedIds={selectedIds}
      hoveredId={hoveredId}
      rowSlots={(node: HierarchyNode) => {
        const state = attention[node.refid]?.state;
        return observerSlots({
          kind: node.kind,
          observerCount: observerCounts[node.refid] ?? 0,
          countsLoading,
          attention: attention[node.refid],
          whereText: whereFor(node.refid),
          dot: state && !countsLoading ? STATUS_DOT[state] : null,
        });
      }}
      onToggle={onToggle}
      onSelect={onSelect}
    />
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Cameras view — press a feed, set up observers on it                */
/* ------------------------------------------------------------------ */

const FeedList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  margin: 0;
  padding: ${space[2]} ${space[3]} ${space[3]};
  list-style: none;

  .feed-head {
    display: flex;
    justify-content: space-between;
    padding: 0 ${space[2]};
    font-size: ${typeRole.caption1.fontSize};
    color: ${semantic.text.muted};
    em {
      font-style: italic;
    }
  }
`;

/* The loading placeholder for the Cameras view — same rhythm as `FeedList`, so the column does not
   jump when the real feeds arrive. */
const FeedSkeletons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  padding: 0 ${space[3]} ${space[3]};
`;

const FeedBlock = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};

  .feed-row {
    display: grid;
    grid-template-columns: 12px 58px 1fr 40px;
    align-items: center;
    gap: ${space[2]};
    width: 100%;
    padding: ${space[1]} ${space[2]};
    border: 0;
    border-radius: ${radius.md};
    background: transparent;
    text-align: left;
    cursor: pointer;
    color: ${semantic.text.primary};
    &:hover {
      background: ${rgba(semantic.surface.base, 0.8)};
    }
    &:focus-visible {
      outline: none;
      box-shadow: ${focus.ring};
    }
    &.is-current {
      background: ${rgba(brand.frost, 0.9)};
      .feed-name {
        font-weight: ${weight.semibold};
        color: ${inkOn.aqua};
      }
      .thumb {
        box-shadow: 0 0 0 1.5px ${semantic.status.selected.edge};
      }
    }
  }
  /* Card 21's Thumb (52 px, 16:9): \`CameraPicture size="thumb"\` letterboxes the frame inside
     this box at its own ratio and draws its own no-frame placeholder — it used to be a 58 × 38 crop
     (\`camera-feeds-page-wall-tile\`). The ring hugs the box at the Thumb's 5 px radius. */
  .thumb {
    display: block;
    flex: none;
    width: 52px;
    aspect-ratio: 16 / 9;
    border-radius: 5px;
    box-shadow: 0 0 0 1.5px ${semantic.surface.base};
  }
  .feed-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .feed-name {
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    overflow-wrap: anywhere;
  }
  .feed-age {
    font-size: ${typeRole.caption2.fontSize};
    line-height: ${typeRole.caption2.lineHeight};
    font-weight: ${weight.medium};
    color: ${semantic.text.muted};
    &.is-stale {
      color: ${semantic.status.roles.information.ink};
    }
  }
  .feed-count {
    font-family: ${fontMono};
    font-size: ${typeRole.caption1.fontSize};
    font-weight: ${weight.semibold};
    color: ${semantic.text.secondary};
    text-align: right;
  }
  .camera-lists {
    padding-left: ${space[5]};
  }
`;

const EmptyNote = styled.p`
  margin: 0;
  padding: ${space[2]};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.muted};
`;

interface CamerasViewProps {
  feeds: CameraWallFeed[];
  /**
   * How the stream read went — the SAME three states the picker takes, not a boolean.
   *
   * ⚠ It was `streamsFailed: boolean` and that was the exact defect this whole feature exists to
   * remove, reintroduced in its own new code: while the read is in flight `feeds` is `[]` and
   * `failed` is false, so the view rendered the authored sentence "This twin has no camera feeds"
   * — an affirmative false claim about a customer's gym, on a read that has not answered yet.
   */
  streamsStatus: 'loading' | 'ready' | 'failed';
  /** Observers per feed, built in one pass by the host rather than scanned per feed per render. */
  observersByFeed: Record<number, number>;
  /** Set up observers on this feed: the host opens the camera workspace on it. */
  onOpenFeed: (feedId: number) => void;
  /**
   * Re-fetch one feed's picture from the edge.
   *
   * The same control the picker's tiles carry, and for the same reason: a stored frame can be days
   * old — measured on staging PUL-CON, every one of its eight feeds was 15 days stale — and an
   * operator deciding which camera to set up needs to be able to ask for a current one.
   */
  onRefreshFeed: (feedId: CameraWallFeed['id']) => void;
  /** Each feed's observer regions, for its tile's hover zoom (`camera-zoom-dwell-and-observers`). */
  zoomRegionsByFeed?: ReadonlyMap<number, readonly FrameRegion[]>;
  /** The camera on the stage, camera by camera: its row is current and expands (FR19). */
  currentFeedId?: number | null;
  /**
   * How the observers read went. The counts are an answer only once it is `ready`: before, and on
   * a failure, no row shows a count or says *no observers* (Failure States, "Live observers").
   */
  observersStatus?: 'pending' | 'ready' | 'failed';
  /** What the current camera's row expands to: the entities on it and likely in view. */
  current?: React.ReactNode;
}

const countText = (count: number): string =>
  count === 0 ? 'no observers' : count === 1 ? '1 observer' : `${count} observers`;

/** A feed's picture age for its row (FR19): the capture time, or the wall's own stale label. */
const pictureLine = (feed: CameraWallFeed): string =>
  !feed.imageUrl
    ? 'No picture'
    : feed.state === 'warning' && feed.statusLabel
      ? feed.statusLabel
      : feed.capturedAt
        ? `Picture ${clockTime(feed.capturedAt)}`
        : 'Picture';

/**
 * Camera by camera (`observers-page-camera-stage`, FR19; Rob's v3 state 07): every feed by name,
 * each row its thumbnail, name, picture age — in the pink ink when the wall calls it stale — and
 * observer count. Pressing a row puts that camera on the stage with nothing picked. The current
 * camera's row expands to the entities on it and those likely in view, where a press picks one or
 * links the waiting region. Replaces the tiles that opened the camera-workspace pop-up.
 */
const CamerasView: React.FC<CamerasViewProps> = ({
  feeds,
  streamsStatus,
  observersByFeed,
  onOpenFeed,
  currentFeedId = null,
  current,
  observersStatus = 'ready',
}) => {
  if (streamsStatus === 'failed') {
    return (
      <EmptyNote role="status">{`${FEEDS_UNREADABLE_TITLE} ${FEEDS_UNREADABLE_BODY}`}</EmptyNote>
    );
  }

  if (streamsStatus === 'loading') {
    return (
      <>
        <EmptyNote role="status">{FEEDS_LOADING}</EmptyNote>
        <FeedSkeletons aria-hidden="true">
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} shape="line" />
          ))}
        </FeedSkeletons>
      </>
    );
  }

  if (feeds.length === 0) {
    return <EmptyNote role="status">{NO_FEEDS_BODY}</EmptyNote>;
  }

  // A feed whose id is not a number has no camera to open: it is not offered (a row would pass NaN).
  const sorted = feeds
    .filter(feed => Number.isFinite(Number(feed.id)))
    .sort((a, b) => NAME_COLLATOR.compare(a.name, b.name));
  return (
    <FeedList>
      <li className="feed-head" aria-hidden="true">
        <span>
          Camera <Icon name="arrow-down-short-wide" size="xs" /> <em>by name</em>
        </span>
        <span>Observers</span>
      </li>
      {sorted.map(feed => {
        const id = Number(feed.id);
        const count = observersStatus === 'ready' ? (observersByFeed[id] ?? 0) : null;
        const isCurrent = id === currentFeedId;
        const age = pictureLine(feed);
        return (
          <FeedBlock key={feed.id} data-endpoint={id}>
            <button
              type="button"
              className={`feed-row${isCurrent ? ' is-current' : ''}`}
              aria-pressed={isCurrent}
              aria-label={[feed.name, count == null ? null : countText(count), age]
                .filter(Boolean)
                .join(', ')}
              onClick={() => onOpenFeed(id)}
            >
              <Icon name={isCurrent ? 'chevron-down' : 'chevron-right'} size="xs" />
              <span className="thumb">
                <CameraPicture feed={feed} size="thumb" />
              </span>
              <span className="feed-text">
                <span className="feed-name">{feed.name}</span>
                <span className={`feed-age${feed.state === 'warning' ? ' is-stale' : ''}`}>
                  {age}
                </span>
              </span>
              <span className="feed-count">{count ?? ''}</span>
            </button>
            {isCurrent && current}
          </FeedBlock>
        );
      })}
    </FeedList>
  );
};

interface Props {
  className?: string;
  /**
   * The floor this panel lists, as a target in its own right (`refiling-preserves-configuration`,
   * epic ledger row 121) — a camera region can count a whole floor. `refid` is `floor:{id}`
   * (`floorTargetRefid`). The row reads *Whole floor — {name}* with no BID (Rob, 2026-10-01). Omitted,
   * no floor row is shown.
   */
  floor?: { refid: string; name: string };
  surfaces: FloorPlannerSurface[];
  items: FloorPlannerItem[];
  /** refid → number of observer polygons drawn against that entity. */
  observerCounts: Record<string, number>;
  /**
   * The twin's live observers are still being read, so `observerCounts` is not yet the answer.
   *
   * Rows say they are still counting rather than showing nothing, which is exactly what a row with
   * no observers looks like — FR13's rendered half, in the one place an operator reads a count.
   */
  countsLoading?: boolean;
  /**
   * Per-entity attention state (`camera-observer-setup-attention`): groups the Inventory view and
   * marks rows in every view. Absent, the panel renders ungrouped and unmarked, as it did before.
   */
  attention?: Record<string, EntityAttention>;
  /** The three attention states counted across the twin, split by the active floor (FR12). */
  /** `checking`: how many of them need checking, which tones the strip a problem (R7). */
  attentionSummary?: { thisFloor: number; otherFloors: number; checking?: number };
  /**
   * How the observers read went — `pending` while counts are unknown (the rows say they are still
   * counting, no groups), `failed` when it broke (no groups, and never "no observer"), `ready`
   * otherwise. A pending or failed read classifies nothing; grouping 160 entities under
   * *No observer* for the seconds a read takes is the defect feature 04 removed, at scale.
   */
  observersStatus?: 'pending' | 'ready' | 'failed';
  /** How the running-model read went — it decides what the *Observer needs checking* header says. */
  modelStatus?: 'loading' | 'none' | 'ready' | 'failed';
  /**
   * How the draft comparison went — it decides what an empty *Added in this draft* group says, so
   * the group never claims "nothing added" while the answer is loading or unknown (Failure States).
   */
  addedStatus?: 'loading' | 'ready' | 'failed';
  selectedId: string | null;
  onSelect: (refid: string, kind: ObserverTargetKind) => void;
  /**
   * Every feed on the twin, for the Cameras view. Omit and that view is not offered — which is what
   * the camera workspace's copy does, where the camera is already chosen.
   */
  feeds?: CameraWallFeed[];
  /** How the stream read went. Defaults to `ready` so a host that omits it is not lying. */
  streamsStatus?: 'loading' | 'ready' | 'failed';
  observersByFeed?: Record<number, number>;
  /** Open the camera workspace on a feed. Required alongside `feeds` for the Cameras view. */
  onOpenFeed?: (feedId: number) => void;
  /** Re-fetch one feed's picture. Required alongside `feeds` for the Cameras view to be offered. */
  onRefreshFeed?: (feedId: CameraWallFeed['id']) => void;
  /**
   * Each feed's observer regions, drawn in its Cameras-view tile's hover zoom
   * (`camera-zoom-dwell-and-observers`). Built with `zoomRegionsByCamera`; compared by identity.
   */
  zoomRegionsByFeed?: ReadonlyMap<number, readonly FrameRegion[]>;
  /**
   * What the camera workspace's camera sees on the plan (`camera-first-observers`, R13-R18).
   * `in-view`: both views list only those zones and machines, with *Show everything* to list the
   * rest. `unplaced`: the camera is not on the plan, so everything is listed and the line says why.
   * Absent (the stage): no filter and no line.
   */
  sight?: { kind: 'in-view'; refids: ReadonlySet<string> } | { kind: 'unplaced' };
  /**
   * The camera on the stage, camera by camera (`observers-page-camera-stage`, FR19): its Cameras-view
   * row is current and expands to the entities on it and those likely in view.
   */
  currentFeedId?: number | null;
  /** A region drawn first is waiting for its entity: the list prompts for one, in every view. */
  waiting?: boolean;
  /**
   * The camera workspace only (`workspace-inventory-this-camera`): each entity's complete regions on
   * the workspace's camera. A placed camera's lists add these to the cone, and every row with an
   * observer says where its observers are. Absent (the stage): neither.
   */
  observersOnThisCamera?: Readonly<Record<string, number>>;
  /** The entity under the plan's pointer (observers-page-stage, FR15): its row shows its hover look. */
  hoveredId?: string | null;
  /**
   * Where the header part goes (`onboarding-persistent-scene-observers-glass`, R2.2): the list
   * panel's header, under its search row. The view tabs, the sight filter, the waiting prompt, the
   * attention notice and the sort headings render there, out of the scroll; the rows stay here. The
   * state behind them stays in this one component. Absent, the header renders at the top of the
   * panel, as it did.
   */
  headerHost?: HTMLElement | null;
}

/**
 * Left-hand panel for the Observers step. Three views:
 *
 * - **Inventory** — every entity grouped by attention state, in the FR8 order.
 * - **Hierarchy** — the same entities nested by their parent zone.
 * - **Cameras** — the camera-first way in: pressing a feed opens the camera workspace on it.
 *
 * Selecting an entity in the first two opens its observer editor (on the stage) or picks it (in the
 * camera workspace, which does not offer the Cameras view).
 */
/**
 * The groups of the Inventory view, in order.
 *
 * *Totalled by a complex measure* sits between *No observer* and *Observer needs checking*: it is
 * the answer to the same question those two ask — what is measuring this? — and putting it beside
 * them is what stops an operator reading the gap list as longer than it is.
 */
const GROUP_COPY: Record<(typeof GROUP_ORDER)[number], { emptyCopy: string }> = {
  'added-unobserved': { emptyCopy: 'Nothing added in this draft' },
  check: { emptyCopy: 'Nothing has moved since its observer was drawn' },
  unobserved: { emptyCopy: 'Every entity has an observer' },
  totalled: { emptyCopy: 'Nothing is totalled by a complex measure' },
  configured: { emptyCopy: 'Nothing configured yet' },
};

/**
 * The groups of the Inventory view: the v3 page's order, titles and tones (observers-page-stage,
 * FR9, from #1865) over the same membership rules as before. Titles and tones come from
 * {@link GROUP_LOOK}, which the plan's hover card and legend also read.
 */
const GROUPS: {
  state: AttentionState;
  title: string;
  tone: TagTone;
  emptyCopy: string;
}[] = GROUP_ORDER.map(state => ({ state, ...GROUP_LOOK[state], ...GROUP_COPY[state] }));

/** FR12's sentence, exactly. */
export const attentionStatusText = (
  observersStatus: 'pending' | 'ready' | 'failed',
  modelStatus: 'loading' | 'none' | 'ready' | 'failed',
  summary: { thisFloor: number; otherFloors: number },
): string => {
  if (observersStatus === 'pending') return 'Checking observers…';
  if (observersStatus === 'failed') return 'Observers could not be read';
  const unknown = modelStatus === 'failed' ? ' · changes unknown' : '';
  if (summary.thisFloor + summary.otherFloors === 0) {
    // Not "configured" while the running twin is still being compared — nothing has been shown
    // to have moved yet, which is a different fact. Found by Copilot on the PR.
    return modelStatus === 'loading'
      ? 'Nothing needs attention so far · checking for changes'
      : `Every entity is configured${unknown}`;
  }
  const n = summary.thisFloor;
  const subject = n === 1 ? '1 entity needs' : `${n} entities need`;
  return `${subject} attention on this floor · ${summary.otherFloors} on other floors${unknown}`;
};

/** The row motion's groups when the list is not grouped: one stable empty map, so nothing moves. */
const NO_MOTION_GROUPS: ReadonlyMap<string, string> = new Map();

/**
 * The header part (`onboarding-persistent-scene-observers-glass`, R2.2): in the list panel's header,
 * under its search row, which gives the inset. Top to bottom as frame 03 has it: the view tabs, the
 * sight filter and the waiting prompt where they apply, the attention notice, and the sort headings.
 * The glass header draws no rules between them; the tabs carry their own shelf line.
 */
const HeaderPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  flex-shrink: 0;

  .panel-header {
    display: flex;
    flex-direction: column;
  }

  .sight-filter {
    display: flex;
    flex-direction: column;
    gap: ${space[1]};
  }

  .sight-status {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .attention-status {
    margin: 0;
    ${typeRole.caption1};
    font-weight: ${weight.semibold};
    color: ${semantic.text.secondary};
  }

  .sort-control {
    display: flex;
    gap: ${space[1]};
  }
`;

const ObserverInventoryPanel: React.FC<Props> = ({
  className,
  floor,
  surfaces,
  items,
  observerCounts,
  countsLoading = false,
  attention,
  attentionSummary = { thisFloor: 0, otherFloors: 0 },
  observersStatus = 'ready',
  modelStatus = 'none',
  addedStatus = 'ready',
  selectedId,
  onSelect,
  feeds,
  streamsStatus = 'ready',
  observersByFeed,
  onOpenFeed,
  onRefreshFeed,
  zoomRegionsByFeed,
  sight,
  observersOnThisCamera,
  hoveredId = null,
  currentFeedId = null,
  waiting = false,
  headerHost,
}) => {
  /* The host's handler, read through a ref, so a memoised row (`@repo/ui` HierarchyEntityRow) is never stale. */
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const select = useCallback(
    (refid: string, kind: ObserverTargetKind) => onSelectRef.current(refid, kind),
    [],
  );
  const [view, setView] = useState<View>('inventory');
  /* The list's order (FR12, from #1865): by type unless chosen; the active key again reverses it. */
  const [sortKey, setSortKey] = useState<InventorySortKey>('type');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const attentionText = attentionStatusText(observersStatus, modelStatus, attentionSummary);
  const chooseSort = (key: InventorySortKey) => {
    if (key === sortKey) setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };
  /* Starts narrowed on every camera: the widened lists belong to the camera they were widened on. */
  const [showEverything, setShowEverything] = useState(false);
  const [everythingFor, setEverythingFor] = useState(currentFeedId);
  if (!Object.is(everythingFor, currentFeedId)) {
    setEverythingFor(currentFeedId);
    setShowEverything(false);
  }
  /*
   * What a placed camera lists (R1): the cone, plus every entity with a region on this camera —
   * wherever its plan geometry sits, because a cone is an approximation and an observer this camera
   * carries must be reachable from it — plus the pick, so a row never vanishes under the operator.
   * In-view entities observed only by OTHER cameras stay: a second observer is ordinary (Rob,
   * 2026-10-01), and their rows say where their observers are instead.
   */
  // The cone's own set, not `sight`: the host builds that object inline on every render.
  const cone = sight?.kind === 'in-view' ? sight.refids : null;
  const listed = useListedSet(cone, observersOnThisCamera, selectedId);
  /** The set the lists are narrowed to, or `null` when they list everything. */
  const narrowTo = listed && !showEverything ? listed : null;
  /*
   * The Cameras view needs a host that holds the twin's feeds and can open the workspace on one.
   * The workspace's own copy has neither and does not offer the view, rather than offering a
   * broken one.
   */
  const canShowCameras = !!feeds && !!onOpenFeed && !!onRefreshFeed;
  const tree = useMemo(
    () => buildHierarchyTree(surfaces, items, OBSERVER_LABELS),
    [surfaces, items],
  );
  // A `Set` lookup over a tree built once per floor — never per pointer move (NFR1).
  const shownTree = useMemo(
    () => (narrowTo ? pruneTreeToSight(tree, narrowTo) : tree),
    [tree, narrowTo],
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Default every zone open the first time the tree is shown / changes shape.
  const allZoneIds = useMemo(() => surfaces.map(s => s.refid).join(','), [surfaces]);
  React.useEffect(() => {
    setExpanded(new Set(surfaces.map(s => s.refid)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allZoneIds]);

  const toggle = (refid: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(refid)) next.delete(refid);
      else next.add(refid);
      return next;
    });

  // Groups and markers need a classification to render; a host that supplies none gets the
  // ungrouped list it always had, never four groups with everything called configured.
  const grouped = observersStatus === 'ready' && attention != null;
  const attentionOrNone = attention ?? {};
  /*
   * The floor as a target of its own, above the entity groups rather than inside them: the
   * attention classification is computed over zones and machines, and a floor with no camera of its
   * own is the ordinary case — filing it under "No observer" would flag nearly every floor in the
   * estate as needing attention.
   */
  /*
   * Where each row's observers are (R4) — only in the camera workspace, and only once the observers
   * read has answered: on a pending or failed read, "only on other cameras" would be a claim about
   * an answer nobody has.
   */
  const showWhere = !!observersOnThisCamera && observersStatus === 'ready';
  // One rule for every row's "where are its observers" — the Inventory rows' memo reads it too.
  const whereFor = useCallback(
    (refid: string): string =>
      showWhere ? whereText(observersOnThisCamera?.[refid] ?? 0, observerCounts[refid] ?? 0) : '',
    [showWhere, observersOnThisCamera, observerCounts],
  );

  const floorRows: Row[] = floor
    ? [
        {
          refid: floor.refid,
          kind: 'floor',
          // Says what it is (Rob, 2026-10-01): it is how a whole-floor observer is drawn and
          // edited, and a bare floor name with its BID read like one more zone.
          label: `Whole floor — ${floor.name}`,
          observerCount: observerCounts[floor.refid] ?? 0,
          where: whereFor(floor.refid),
        },
      ]
    : [];
  const floorSection = floor ? (
    <StyledSection
      title="Floor"
      icon={<KindGlyph kind="floor" />}
      rows={floorRows}
      countsLoading={countsLoading}
      selectedId={selectedId}
      onSelect={select}
    />
  ) : null;

  /*
   * The rows, sorted and narrowed, memoised on the data — never on the plan's hover, which re-renders
   * this panel to move a row's glow (observers-page-stage measured the rebuild in every hover frame).
   */
  const allRows = useMemo(() => {
    const zoneRows: Row[] = surfaces.map(s => ({
      refid: s.refid,
      kind: 'surface',
      label: surfaceLabel(s),
      code: s.entityCode,
      observerCount: observerCounts[s.refid] ?? 0,
      attention: grouped ? attention?.[s.refid] : undefined,
      where: whereFor(s.refid),
    }));
    const equipmentRows: Row[] = items.map(i => ({
      refid: i.refid,
      kind: 'item',
      label: itemLabel(i),
      code: i.entityCode,
      observerCount: observerCounts[i.refid] ?? 0,
      attention: grouped ? attention?.[i.refid] : undefined,
      where: whereFor(i.refid),
    }));
    const sorted = sortInventoryRows(
      [...zoneRows, ...equipmentRows].map(row => ({ ...row, name: row.label, bid: row.code })),
      sortKey,
      sortDirection,
    );
    return narrowTo ? filterRowsToSight(sorted, narrowTo) : sorted;
  }, [
    surfaces,
    items,
    observerCounts,
    grouped,
    attention,
    whereFor,
    sortKey,
    sortDirection,
    narrowTo,
  ]);
  /** Each Inventory group's rows, split once per change of the rows. */
  const rowsByGroup = useMemo(() => {
    const by = new Map<AttentionState, Row[]>();
    for (const row of allRows) {
      const state = row.attention?.state ?? 'configured';
      by.set(state, [...(by.get(state) ?? []), row]);
    }
    return by;
  }, [allRows]);

  /*
   * Rows that change group slide there and glow (`observers-page-motion`, FR12–FR16). Only the
   * Inventory view groups; the epoch is everything that rebuilds the list rather than moving a row
   * in it, so a view, sort, floor, widening or camera change never animates.
   */
  const motionGroups = useMemo(
    () =>
      grouped && view === 'inventory'
        ? new Map(
            allRows.flatMap(row => {
              const state = row.attention?.state ?? 'configured';
              // A row still being judged is in no shown group: arriving there, or leaving it, is no move.
              return state === 'pending' ? [] : [[row.refid, state] as const];
            }),
          )
        : NO_MOTION_GROUPS,
    [grouped, view, allRows],
  );
  const motionEpoch = [
    view,
    sortKey,
    sortDirection,
    floor?.refid ?? '',
    grouped,
    showEverything,
    currentFeedId ?? '',
  ].join('|');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { moved } = useRowMotion(scrollRef, { groups: motionGroups, epoch: motionEpoch });
  // A row that moves group is remounted in its new list: focus goes with it, not to the page.
  useKeepFocusAcrossMove(scrollRef);
  /* Said once per move, in a region of its own so it never replaces the count line (A4). */
  const motionText = useRowMotionAnnouncement(moved, refid => {
    const row = allRows.find(r => r.refid === refid);
    const state = row?.attention?.state ?? 'configured';
    return row && state !== 'pending' ? `${row.label} moved to ${GROUP_LOOK[state].title}` : null;
  });

  /*
   * The line above the lists says why an unplaced camera lists everything (R17). A placed camera has
   * no sentence, just *Show everything*: its counts added nothing (Rob, 2026-10-01).
   */
  const floorName = floor?.name ?? 'this floor';
  const sightText =
    sight?.kind === 'unplaced'
      ? `This camera isn't placed on the plan, so everything on ${floorName} is listed.`
      : null;
  /*
   * *Show everything on {floor}* (v3 state 07): the design's text row, in place of the workspace's
   * tick box. Pressed, the camera's lists widen to the whole floor; pressed again they narrow back.
   */
  const showEverythingButton = (
    <Button
      type="button"
      variant="text"
      size="xs"
      // An action whose label says what it will do next: not a toggle, so no pressed state.
      iconLeft={<Icon name={showEverything ? 'minus' : 'plus'} size="xs" />}
      onClick={() => setShowEverything(v => !v)}
    >
      {showEverything ? 'Show less' : `Show everything on ${floorName}`}
    </Button>
  );
  /*
   * The current camera's row, expanded (FR19): the entities with a region on it, then the rest of
   * what it lists — the cone, or the whole floor for a camera with no placement. A press on one is
   * the stage's pick on this camera, or links the waiting region.
   */
  // On the data, never the plan's hover (see `allRows`), and only while the lists are shown.
  const showsCameraLists = currentFeedId != null && !!sight;
  const { onCamera, likely } = useMemo(
    () =>
      showsCameraLists
        ? {
            onCamera: allRows.filter(r => (observersOnThisCamera?.[r.refid] ?? 0) > 0),
            likely: allRows.filter(r => (observersOnThisCamera?.[r.refid] ?? 0) === 0),
          }
        : { onCamera: allRows, likely: allRows },
    [showsCameraLists, allRows, observersOnThisCamera],
  );
  const cameraLists =
    currentFeedId != null && sight ? (
      <div className="camera-lists">
        {floorSection}
        {sight.kind === 'unplaced' ? (
          <StyledSection
            title={`Everything on ${floorName}`}
            icon={null}
            rows={allRows}
            countsLoading={countsLoading}
            hoveredId={hoveredId}
            selectedId={selectedId}
            onSelect={select}
          />
        ) : (
          <>
            <StyledSection
              title="On this camera"
              icon={null}
              tone="neutral"
              rows={onCamera}
              emptyCopy="No regions on this camera yet"
              countsLoading={countsLoading}
              hoveredId={hoveredId}
              selectedId={selectedId}
              onSelect={select}
            />
            <StyledSection
              title={showEverything ? `Everything else on ${floorName}` : 'Likely in view'}
              icon={null}
              tone="lavender"
              rows={likely}
              emptyCopy={NONE_IN_VIEW}
              countsLoading={countsLoading}
              hoveredId={hoveredId}
              selectedId={selectedId}
              onSelect={select}
            />
            {showEverythingButton}
          </>
        )}
      </div>
    ) : null;

  // Rows that carry no marking — the tree gets these while nothing is known.
  const rowAttention = grouped ? attentionOrNone : {};

  /* The *Observer needs checking* header says what it knows: a count once the running model has
     been read, a wait while it is being read, and an honest "unknown" when it could not be. An
     empty group with a zero count would claim nothing moved, which is the Failure States table's
     forbidden rendering of an errored read. */
  const checkGroup = (
    group: (typeof GROUPS)[number],
  ): { title: string; hideCount: boolean; emptyCopy: string } =>
    modelStatus === 'loading'
      ? {
          title: 'Checking for changes…',
          hideCount: true,
          emptyCopy: 'Still comparing the plan with the running twin',
        }
      : modelStatus === 'failed'
        ? {
            title: 'Unknown — the running twin could not be read',
            hideCount: true,
            emptyCopy: 'Nothing could be compared, so nothing here is known to be right',
          }
        : { title: group.title, hideCount: false, emptyCopy: group.emptyCopy };

  /* An empty *Added in this draft* group is only "nothing added" once the draft has been compared
     with production; before that, or when it could not be, the count is hidden and the copy says
     why, narrowed to a camera or not, since a zero there would be unverified. */
  const addedGroup = (
    group: (typeof GROUPS)[number],
  ): { title: string; hideCount: boolean; emptyCopy: string } =>
    addedStatus === 'loading'
      ? {
          title: group.title,
          hideCount: true,
          emptyCopy: 'Still comparing the draft with production',
        }
      : addedStatus === 'failed'
        ? {
            title: group.title,
            hideCount: true,
            emptyCopy: 'The draft could not be compared with production, so this is not known',
          }
        : { title: group.title, hideCount: false, emptyCopy: group.emptyCopy };

  const viewPanelId = useId();
  const header = (
    <HeaderPart data-observer-inventory-header="">
      <div className="panel-header">
        <Tabs
          aria-label="Inventory views"
          // One panel shows whichever view is chosen: the rows' scroll below (`aria-controls`).
          tabs={[
            { id: 'inventory', label: 'Inventory', panelId: viewPanelId },
            { id: 'hierarchy', label: 'Hierarchy', panelId: viewPanelId },
            ...(canShowCameras ? [{ id: 'cameras', label: 'Cameras', panelId: viewPanelId }] : []),
          ]}
          value={view}
          onChange={id => setView(id as View)}
        />
      </div>

      {sight && (
        <div className="sight-filter">
          {/* Polite; mounts with the unplaced camera's view, so its text settles after the mount. */}
          {sightText !== null && (
            <p className="sight-status" role="status">
              <SettledText text={sightText} />
            </p>
          )}
          {/* No tick box for an unplaced camera: there is nothing to switch between (R17). */}
          {sight?.kind === 'in-view' && view !== 'cameras' && showEverythingButton}
        </div>
      )}

      {waiting && (
        /* A region drawn first waits for its entity (FR19): any list's row links it. */
        <div className="list-prompt">
          <Banner tone="info" surface="on-glass" role="status" icon={<Icon name="hand-pointer" />}>
            Pick the entity this region watches
          </Banner>
        </div>
      )}

      {/* FR12: the one place the count crosses floors, as text, announced politely when it changes.
            While something needs attention it is the design's attention strip, a Note in the stronger
            role it counts: a problem when anything needs checking, else work to do
            (onboarding-status-colours-observers, R7); otherwise the quiet line it always was. ⚠ Those two are what
            is SEEN; what is SAID is one live region that never unmounts — swapping the strip for the
            line would insert a filled region at exactly the change it exists to announce. */}
      {view !== 'cameras' &&
      observersStatus === 'ready' &&
      attentionSummary.thisFloor + attentionSummary.otherFloors > 0 ? (
        <div className="attention-strip" aria-hidden="true">
          <Note tone={(attentionSummary.checking ?? 0) > 0 ? 'pink' : 'peri'}>{attentionText}</Note>
        </div>
      ) : (
        <p className="attention-status" aria-hidden="true">
          {attentionText}
        </p>
      )}

      {view === 'inventory' && grouped && (
        <div className="sort-control" role="group" aria-label="Sort the list">
          {(
            [
              ['name', 'Name'],
              ['type', 'by type'],
              ['bid', 'BID'],
            ] as const
          ).map(([key, text]) => (
            <Button
              key={key}
              type="button"
              variant="text"
              size="xs"
              aria-pressed={sortKey === key}
              aria-label={
                sortKey === key
                  ? `${text}, ${sortDirection === 'asc' ? 'ascending' : 'descending'}`
                  : undefined
              }
              iconRight={
                sortKey === key ? (
                  <Icon
                    name={sortDirection === 'asc' ? 'arrow-down-short-wide' : 'sort'}
                    size="xs"
                  />
                ) : undefined
              }
              onClick={() => chooseSort(key)}
            >
              {text}
            </Button>
          ))}
        </div>
      )}
    </HeaderPart>
  );

  return (
    <aside className={className} data-observer-inventory="">
      {headerHost ? createPortal(header, headerHost) : header}
      <span className="attention-live" role="status" data-testid="attention-status">
        {attentionText}
      </span>
      <span className="attention-live" role="status" data-testid="motion-status">
        {motionText}
      </span>

      {/* Focusable by script only: where focus lands when the row it was meant for is not rendered. */}
      <div
        className="panel-scroll"
        tabIndex={-1}
        ref={scrollRef}
        role="tabpanel"
        id={viewPanelId}
        aria-label={VIEW_LABELS[view]}
      >
        {view === 'cameras' && canShowCameras ? (
          <CamerasView
            feeds={feeds ?? []}
            streamsStatus={streamsStatus}
            observersByFeed={observersByFeed ?? {}}
            onOpenFeed={onOpenFeed!}
            onRefreshFeed={onRefreshFeed!}
            zoomRegionsByFeed={zoomRegionsByFeed}
            currentFeedId={currentFeedId}
            current={cameraLists}
            observersStatus={observersStatus}
          />
        ) : view === 'inventory' ? (
          grouped ? (
            <>
              {floorSection}
              {GROUPS.map(group => {
                const header =
                  group.state === 'check'
                    ? checkGroup(group)
                    : group.state === 'added-unobserved'
                      ? addedGroup(group)
                      : { title: group.title, hideCount: false, emptyCopy: group.emptyCopy };
                return (
                  <StyledSection
                    key={group.state}
                    title={header.title}
                    hideCount={header.hideCount}
                    icon={null}
                    tone={group.tone}
                    hoveredId={hoveredId}
                    typeHeadings={sortKey === 'type'}
                    rows={rowsByGroup.get(group.state) ?? NO_ROWS}
                    // Narrowed, an empty group is a fact about the camera's cone: "Every entity has
                    // an observer" would be a false claim about the floor (R14).
                    emptyCopy={narrowTo && !header.hideCount ? NONE_IN_VIEW : header.emptyCopy}
                    countsLoading={countsLoading}
                    selectedId={selectedId}
                    onSelect={select}
                  />
                );
              })}
            </>
          ) : (
            /* Nothing is known yet (or the read broke), so no groups: one list, every row saying
               it is still counting — or, on a failed read, saying nothing at all rather than
               "no observer". */
            <>
              {floorSection}
              <StyledSection
                title={
                  observersStatus === 'failed'
                    ? 'Observers could not be read'
                    : 'Checking observers…'
                }
                icon={<Icon name="eye" variant="solid" size="xs" />}
                hoveredId={hoveredId}
                rows={allRows}
                // Narrowed to a cone that holds nothing, the default "None placed yet" would claim
                // the floor is empty while the read is still out (R14, R18).
                emptyCopy={narrowTo ? NONE_IN_VIEW : undefined}
                countsLoading={countsLoading}
                selectedId={selectedId}
                onSelect={select}
              />
            </>
          )
        ) : (
          <div className="tree">
            {floorRows.map(row => (
              <ObserverEntityRow
                key={row.refid}
                refid={row.refid}
                kind={row.kind}
                label={row.label}
                code={row.code}
                observerCount={row.observerCount}
                countsLoading={countsLoading}
                whereText={row.where}
                selected={selectedId === row.refid}
                leading={<span className="expander-spacer" />}
                onSelect={() => select(row.refid, row.kind)}
              />
            ))}
            {tree.length === 0 ? (
              <p className="empty-row">Nothing placed yet</p>
            ) : shownTree.length === 0 ? (
              <p className="empty-row">Nothing on the plan is in view of this camera</p>
            ) : (
              <ObserverTreeRows
                nodes={shownTree}
                depth={0}
                expanded={expanded}
                observerCounts={observerCounts}
                attention={rowAttention}
                whereFor={whereFor}
                countsLoading={countsLoading}
                selectedId={selectedId}
                hoveredId={hoveredId}
                onToggle={toggle}
                onSelect={select}
              />
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default styled(ObserverInventoryPanel)`
  /* The list panel's body sizes it (360, onboarding-persistent-scene-observers-glass R2.1). */
  width: 100%;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  /* The surface is the host's: the step's list panel is the glass, so the panel itself draws none. */
  min-height: 0;
  overflow: hidden;

  .attention-live {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .panel-scroll {
    position: relative;
    flex: 1;
    overflow-y: auto;
  }

  /* Where a row that changed group was: a dashed mint pill, closing behind it (v3 L1512). */
  ${rowMotionGapLook('2px 10px 2px 44px')}

  /* The observer slots' styles (moved here from the row, which is shared now): the count, the
     still-counting placeholder and the state lines inside each row's button. */
  .row-observers {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${semantic.text.secondary};

    svg {
      width: 13px;
      height: 13px;
    }
  }

  /* Still reading the twin's observers. Greyed so it does not read as a count, and it carries its
     own text for a screen reader — the ellipsis alone announces as nothing.

     A token, as every colour in this file now is but the decorative greys of its rules and
     placeholder text; a new hard-coded colour is a gate this repo does not make exceptions to. */
  .row-observers.counting {
    color: ${semantic.text.placeholder};
  }

  /* The attention marker: the glyph in its state's role ink (AttentionGlyph), the words in the
     row's own ink. Each role ink on the page surface is well above 4.5:1; it is the FILLED chip with
     small text inside it that ruling R5 rules out (3.2:1), so no fill here. */
  .row-state {
    /* Its own line, under the name and code: measured on the 300 px panel, inline it squeezed
       "Cardio 1" to "C…", and the name is the thing an operator reads. Tokens, not the file's
       usual literals — R5 excuses what was here, not what is added. */
    flex-basis: 100%;
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    ${typeRole.captionCompact};
    font-weight: ${weight.semibold};
    white-space: nowrap;
  }

  /* "totalled by a complex measure" and "measured in N parts" are statements about what IS
     measured, so they take the row's quiet secondary ink rather than the attention marker's
     treatment above. Colour is not the channel either way — the words carry it, which is what a
     screen reader gets. (No backticks in here: this is inside a styled-components template, and
     one would close it.) */
  .row-state.calm {
    color: ${semantic.text.secondary};
    font-weight: ${weight.regular};
  }

  .tree {
    padding: 0.5rem 0.75rem;
  }

  .empty-row {
    margin: 0;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #a3a3a3;
    font-style: italic;
  }
`;
